<template>
  <div class="sci-page-wrapper" style="font-family: 'Inter', sans-serif;">
    <div class="max-w-7xl mx-auto space-y-8">
      
      <!-- HEADER -->
      <div class="sci-page-header pb-4">
        <h1 class="sci-page-title-only">Relatório TCE: Abastecimento</h1>
        <p class="text-sm text-slate-500">Consulta e exportação de dados de abastecimento para o Tribunal de Contas</p>
      </div>

      <!-- ÁREA DE FILTRO -->
      <section class="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-custom">
          <div class="px-6 py-4" style="background-color: #333333;">
              <h2 class="text-white font-bold flex items-center gap-2 uppercase tracking-widest text-[11px]">
                  <v-icon color="orange" small class="mr-2">mdi-magnify</v-icon>
                  FILTRAR CONSULTA
              </h2>
          </div>
          <div class="px-6 pt-6 pb-10">
              <v-row>
                <v-col cols="12" sm="4">
                  <DatePicker
                    @dateValue="[(filter.date = $event)]"
                    label="Mês e Ano"
                    type="month"
                  />
                </v-col>
                <v-col cols="12" sm="4">
                  <DatePicker
                    @dateValue="[(filter.data_inicial = $event)]"
                    label="Data Inicial"
                    type="date"
                  />
                </v-col>
                <v-col cols="12" sm="4">
                  <DatePicker
                    @dateValue="[(filter.data_final = $event)]"
                    label="Data Final"
                    type="date"
                  />
                </v-col>
                <v-col cols="12" sm="6">
                  <v-autocomplete
                    :items="prefectures"
                    label="Prefeitura"
                    v-model="filter.prefecture_id"
                    item-value="prefecture_id"
                    item-text="prefecture_social_name"
                    outlined
                    dense
                    @change="getCode(filter.prefecture_id)"
                  />
                </v-col>
                <v-col cols="12" sm="6">
                  <v-autocomplete
                    :items="codigos"
                    label="Código da Unidade Gestora"
                    v-model="filter.cod"
                    item-value="cod"
                    item-text="cod"
                    outlined
                    dense
                    hide-details
                  />
                </v-col>
              </v-row>

              <div class="flex justify-end gap-3 mt-8">
                  <v-btn
                    :to="`/report/tce/abastecimento/${filter.date?.substring(5)}/${filter.date?.substring(0, 4)}/${filter.prefecture_id}/${filter.cod}/${filter.data_inicial}/${filter.data_final}`"
                    depressed
                    :disabled="isButtonDisabled"
                    class="bg-slate-100 text-slate-600 font-bold px-6 h-10 rounded-lg transition-all text-[11px] uppercase tracking-widest shadow-none"
                  >
                    Gerar Relatório
                  </v-btn>
                  <button 
                    @click="search"
                    :disabled="isButtonDisabled"
                    class="bg-orange-500 hover:bg-orange-600 text-white rounded-lg shadow-md shadow-orange-500/20 flex items-center justify-center gap-2 w-fit whitespace-nowrap px-8 h-10 transition-all transform hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed text-[11px] font-bold uppercase tracking-widest"
                  >
                      <v-icon color="white" small class="mr-1">mdi-filter-variant</v-icon>
                      FILTRAR
                  </button>
              </div>
          </div>
      </section>

      <!-- RESULTADO -->
      <section v-if="data.length > 0 || loader" class="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-custom">
          <div class="px-6 py-4 flex items-center justify-between" style="background-color: #333333;">
              <h2 class="text-white font-bold flex items-center gap-2 uppercase tracking-widest text-[11px]">
                  <v-icon color="orange" small class="mr-2">mdi-table-large</v-icon>
                  RESULTADO
              </h2>
              <button 
                @click="exportFile"
                :disabled="isButtonDisabled"
                class="bg-white/10 hover:bg-white/20 text-white font-bold text-[10px] px-4 py-2 rounded-lg border border-white/20 transition-all uppercase tracking-widest flex items-center gap-2 disabled:opacity-50"
              >
                <v-icon small color="white">mdi-download</v-icon>
                Exportar
              </button>
          </div>
          <div class="overflow-x-auto">
              <v-data-table 
                :loading="loader" 
                :items="data" 
                :headers="headers"
                :items-per-page="15"
                class="custom-table"
              >
                <template v-slot:item.categoria="{ item }">
                  <span class="text-xs font-bold px-2 py-1 rounded bg-slate-100 text-slate-600 uppercase">
                    {{ item.categoria == 1 ? "Veículo" : "Máquina" }}
                  </span>
                </template>
                <template v-slot:item.tipoCombustivel="{ item }">
                  <span class="text-xs font-bold px-2 py-1 rounded bg-slate-100 text-slate-600 uppercase">
                    {{
                      item.tipoCombustivel == 1
                        ? "Gasolina"
                        : item.tipoCombustivel == 2
                        ? "Diesel"
                        : item.tipoCombustivel == 3
                        ? "Etanol"
                        : "Gás Natural"
                    }}
                  </span>
                </template>
              </v-data-table>
          </div>
      </section>

      <v-snackbar
        multi-line
        :color="snack.color"
        v-model="snack.show"
        transition="fade-transition"
        top
        right
      >
        {{ snack.message }}
        <template v-slot:action="{ attrs }">
          <v-btn dark text v-bind="attrs" @click="snack.show = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </template>
      </v-snackbar>
    </div>
  </div>
</template>

<script>
import Helpers from "@/mixins/helpers.js";
import DatePicker from "@/components/InputDate.vue";
export default {
  components: { DatePicker },
  mixins: [Helpers],
  data() {
    return {
      loader: false,
      data: [],
      prefectures: [],
      codigos: [],
      snack: {
        show: false,
        message: "",
        color: "",
      },
      filter: {
        prefecture_id: null,
        date: null,
        cod: null,
        data_inicial: null,
        data_final: null,
      },
      headers: [
        { text: "Código da Unidade Gestora", value: "codUnidadeGestora" },
        { text: "Exercício a que se refere a lei", value: "ano" },
        { text: "Mês de referência do abastecimento", value: "mes" },
        { text: "Quantidade em litros", value: "quantidade" },
        { text: "Tipo de combustível", value: "tipoCombustivel" },
        { text: "Categoria do veículo", value: "categoria" },
        { text: "Número da placa da frota", value: "codigo" },
      ],
    };
  },
  created() {
    this.Helpers;
    this.initialize();
  },
  methods: {
    initialize() {
      this.loader = true;
      this.$store.dispatch("setLoader", this.loader);
      this.$http.get("/prefectures").then((r) => (this.prefectures = r.data));
      this.loader = false;
      this.$store.dispatch("setLoader", this.loader);
    },
    getCode(prefecture_id) {
      this.$http(`/tce/codigo/${prefecture_id}`).then(
        (r) => (this.codigos = r.data)
      );
    },
    search() {
      this.loader = true;
      this.$store.dispatch("setLoader", this.loader);
      this.$http
        .get(
          `/tce/abastecimento/${
            this.filter.prefecture_id
          }/${this.filter.date.substring(0, 4)}/${this.filter.date.substring(
            5
          )}/${this.filter.cod}/${this.filter.data_inicial}/${
            this.filter.data_final
          }`
        )
        .then((res) => {
          this.data = res.data;
          this.loader = false;
          this.$store.dispatch("setLoader", this.loader);
        })
        .catch((e) => {
          this.loader = false;
          this.$store.dispatch("setLoader", this.loader);
          this.snack.message = e.message || "Erro ao filtrar dados";
          this.snack.color = "error";
          this.snack.show = true;
        });
    },
    exportFile() {
      const element = document.createElement("a");
      const content = this.data.map((value, index) => {
        if (index > 0) {
          return `\n${value.export}`;
        }
        return value.export;
      });

      const file = new Blob(content, { type: "text/plain" });
      element.href = URL.createObjectURL(file);
      element.download = `${this.filter.cod}${this.filter.date.substring(
        5
      )}${this.filter.date.substring(0, 4)}Abastecimento.txt`;
      document.body.appendChild(element);
      element.click();
    },
  },
  computed: {
    isButtonDisabled() {
      return !(
        this.filter.prefecture_id &&
        this.filter.date &&
        this.filter.cod
      );
    },
  },
};
</script>
